import {
  Flex,
  LinkBox,
  NextLink,
  useBreakpointValue,
  Box,
  Heading,
  Image,
  Text,
  Button,
  Avatar,
  AvatarGroup,
  LinkOverlay,
} from '@dapperlabs/core-fe';
import {
  Card,
  BoxGlow,
  useHover,
  CardMedia,
  CardContent,
  CardFooter,
  DescriptionList,
  DescriptionTerm,
  DescriptionDetails,
  DescriptionDetailsUnit,
} from '@dapperlabs/skeleton-ui';
import { AnimatePresence, motion } from 'framer-motion';
import { Pill } from 'src/general/components/Pill';
import { usdFormatter } from 'src/lib/helpers/currencyFormatter';
import { imageOptimizerCDN } from 'src/lib/ImageOptimizer';
import { useTranslation } from 'src/lib/translations';

const capitalize = (string: string): string =>
  string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
interface MyMomentsListItemProps {
  awayTeam: string;
  badges: string;
  description: string;
  editionId: number;
  homeTeam: string;
  listingPrice?: number;
  matchSeason: string;
  momentImage: string;
  playName: string;
  playType: string;
  playerName: string;
  rarityTier: string;
  serialNumber: number;
  totalBurned: number;
  totalMinted: number;
}

const slideUp = {
  closed: { transition: { type: 'tween' }, y: '0rem' },
  open: { transition: { type: 'tween' }, y: '-4rem', zIndex: 2 },
};

export const MyMomentsListItem = ({
  rarityTier,
  serialNumber,
  totalMinted,
  totalBurned,
  playerName,
  momentImage,
  editionId,
  listingPrice,
  matchSeason,
  playType,
  playName,
  homeTeam,
  awayTeam,
  badges,
}: MyMomentsListItemProps) => {
  const { t } = useTranslation();

  const isMdUp = useBreakpointValue({ md: true });
  const layout = isMdUp ? 'portrait' : 'landscape';

  const [hoverRef, isHovered] = useHover();
  const totalBurnedLine =
    totalBurned === 1
      ? `1 ${t('MarketplaceSearch.MomentsBurned')}`
      : `${totalBurned} ${t('MarketplaceSearch.MomentsBurned_plural')}`;

  const optimizedMomentImage = imageOptimizerCDN({
    // @ts-ignore
    format: 'webp',
    height: 800,
    quality: 50,
    src: momentImage,
    width: 800,
  });

  const description = `${playType} · ${matchSeason} · ${playName}: ${homeTeam} Vs ${awayTeam}`;

  return (
    <LinkBox key={editionId} display='flex' data-testid={editionId}>
      <BoxGlow isAnimated={isHovered} variant={rarityTier.toLowerCase()}>
        <Card
          ref={hoverRef}
          gridTemplateRows='[footer] 1fr'
          borderRadius='xl'
          layout={layout}
          variant='bare'
        >
          <NextLink href={`/moments/${editionId}`} legacyBehavior passHref>
            <CardMedia as='a' position='relative' alignItems='center'>
              {listingPrice && (
                <Pill layout={layout} text={t('ForSaleLabel')} />
              )}
              {optimizedMomentImage && (
                <Image
                  sx={{
                    aspectRatio: '1 / 1',
                  }}
                  w={800}
                  h='auto'
                  bg='black'
                  transform='scale(1.5)'
                  alt='NFT Moment picturing an illustration of a Skeleton'
                  src={optimizedMomentImage}
                />
              )}
            </CardMedia>
          </NextLink>
          <CardContent
            as={motion.div}
            pt={!isMdUp && 3}
            animate={isHovered && isMdUp ? 'open' : 'closed'}
            variants={slideUp}
          >
            <NextLink href={`/moments/${editionId}`} legacyBehavior passHref>
              <Box as='a'>
                <Heading
                  mb={{ base: 1, md: 1.5 }}
                  color='white'
                  fontSize={isMdUp ? 'lg' : 'md'}
                  fontWeight='normal'
                  lineHeight={1}
                  variant='tall'
                >
                  <Box>{playerName}</Box>
                </Heading>
                <Flex
                  align={isMdUp ? 'center' : 'start'}
                  justify='space-between'
                  direction={isMdUp ? 'row' : 'column-reverse'}
                >
                  {(serialNumber || rarityTier || totalMinted) && (
                    <Text
                      mb={isMdUp ? 1.5 : '0.5'}
                      fontSize='sm'
                      fontWeight='bold'
                      size={isMdUp ? 'sm' : 'xs'}
                    >
                      {rarityTier && (
                        <Text
                          as='span'
                          textStyle={rarityTier.toLowerCase()}
                          fontSize='md'
                        >
                          {`${capitalize(rarityTier)} `}
                        </Text>
                      )}
                      #{serialNumber && <>{`${serialNumber}`}</>}
                      {(serialNumber || totalMinted) && <>/</>}
                      {totalMinted && <>{totalMinted}</>}
                    </Text>
                  )}

                  {badges && (
                    <AvatarGroup size='sm' spacing={-2}>
                      <Avatar name='Skeleton' src={badges} />
                    </AvatarGroup>
                  )}
                </Flex>
                <Box>
                  {totalBurnedLine && (
                    <Text
                      color='gray.300'
                      fontSize={isMdUp ? 'md' : 'xs'}
                      noOfLines={1}
                    >
                      {totalBurnedLine}
                    </Text>
                  )}
                  {description && (
                    <Text color='gray.300' fontSize={isMdUp ? 'md' : 'xs'}>
                      {description}
                    </Text>
                  )}
                </Box>
              </Box>
            </NextLink>
          </CardContent>
          <CardFooter
            as={motion.div}
            zIndex='1'
            animate={isHovered && isMdUp ? 'open' : 'closed'}
            variants={slideUp}
          >
            {listingPrice && (
              <NextLink href={`/moments/${editionId}`} legacyBehavior passHref>
                <DescriptionList size={isMdUp ? 'md' : 'sm'} variant='meta'>
                  <DescriptionTerm>
                    {t('ListForSale.modal.inputLabel')}
                  </DescriptionTerm>
                  <DescriptionDetails
                    color={listingPrice ? 'white' : 'gray.300'}
                    fontFamily='body'
                    fontSize={listingPrice && 'md'}
                  >
                    {usdFormatter.format(listingPrice / 100000000)}
                    <DescriptionDetailsUnit color='white'>
                      {` USD`}
                    </DescriptionDetailsUnit>
                  </DescriptionDetails>
                </DescriptionList>
              </NextLink>
            )}
            <AnimatePresence>
              {isHovered && isMdUp && (
                <motion.div
                  key={`view-cta-${editionId}`}
                  transition={{ delay: 0.15 }}
                  animate={{
                    height: 'auto',
                    opacity: 1,
                    visibility: 'visible',
                    y: '0%',
                    zIndex: 2,
                  }}
                  exit={{
                    opacity: 0,
                  }}
                  initial={{
                    height: 0,
                    opacity: 0,
                    visibility: 'hidden',
                    y: '100%',
                  }}
                >
                  <Box pos='absolute' right={0} left={0}>
                    <NextLink
                      href={`/moments/${editionId}`}
                      legacyBehavior
                      passHref
                    >
                      <Button
                        as='a'
                        zIndex='docked'
                        w='full'
                        mt={4}
                        data-edition-id={editionId}
                      >
                        {t('MarketplaceSearch.ViewMomentDetails')}
                      </Button>
                    </NextLink>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
            {!isMdUp && (
              <NextLink href={`/moments/${editionId}`} legacyBehavior passHref>
                <LinkOverlay
                  display='block'
                  overflow='hidden'
                  width='0'
                  height='0'
                >
                  {t('Marketplace.Moments.viewMoment')}
                </LinkOverlay>
              </NextLink>
            )}
          </CardFooter>
        </Card>
      </BoxGlow>
    </LinkBox>
  );
};
